import { useMemo } from 'react';
import { Paper, Title, Text, Stack } from '@mantine/core';
import { PieChart } from '@mantine/charts';
import type { DataLibrarySurveyRow } from '../../types';
import { countByField } from './chartUtils';

interface SatisfactionPieChartProps {
  title: string;
  rows: DataLibrarySurveyRow[];
  field: 'overall_satisfaction' | 'coach_satisfaction';
  order: string[];
  onSegmentClick: (rating: string) => void;
}

export function SatisfactionPieChart({
  title,
  rows,
  field,
  order,
  onSegmentClick,
}: SatisfactionPieChartProps) {
  const data = useMemo(() => countByField(rows, field, order), [rows, field, order]);
  const total = useMemo(() => data.reduce((sum, d) => sum + d.value, 0), [data]);

  if (data.length === 0) {
    return (
      <Paper p="lg" radius="md" withBorder>
        <Title order={4} mb="md">{title}</Title>
        <Text c="dimmed" ta="center" py="xl">No survey responses match these filters</Text>
      </Paper>
    );
  }

  return (
    <Paper p="lg" radius="md" withBorder>
      <Title order={4} mb="md">{title}</Title>
      <PieChart
        size={300}
        data={data}
        withLabels
        withLabelsLine
        labelsPosition="outside"
        labelsType="percent"
        withTooltip
        tooltipDataSource="segment"
        tooltipProps={{
          content: ({ payload }) => {
            if (!payload?.length) return null;
            const item = payload[0];
            const pct = total > 0 ? ((Number(item.value) / total) * 100).toFixed(1) : '0';
            return (
              <Paper p="sm" radius="sm" withBorder shadow="sm" style={{ backgroundColor: '#fff' }}>
                <Stack gap={4}>
                  <Text size="sm">
                    <Text span fw={600}>{title.replace('Satisfaction', 'satisfaction')}: </Text>
                    {item.name}
                  </Text>
                  <Text size="xs">Values: {item.value} records</Text>
                  <Text size="xs">Percent: {pct}%</Text>
                </Stack>
              </Paper>
            );
          },
        }}
        pieProps={{
          onClick: (_entry: unknown, index: number) => {
            const segment = data[index];
            if (segment) onSegmentClick(segment.name);
          },
          style: { cursor: 'pointer' },
        }}
      />
    </Paper>
  );
}
